import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ProductSelection from './Steps/ProductSelection';
import NeedsFinder from './Steps/NeedsFinder';
import SizeSelection from './Steps/SizeSelection';
import ModuleSelection from './Steps/ModuleSelection';
import GlassType from './Steps/GlassType';
import Accessories from './Steps/Accessories';
import Summary from './Steps/Summary';
import StepIndicator from './UI/StepIndicator';
import ProductVisualizer from './UI/ProductVisualizer';
import PriceDisplay from './UI/PriceDisplay';
import { ConfiguratorState, Dimensions, Modules } from '../types';
import { priceService, productCodeFromName } from '../services/priceService';
import {
  mapApiBreakdownToPriceLines,
  sumPriceLines,
  applyCatalogSelection,
  AccessoryCatalogItem,
} from '../services/summaryPriceCalculations';
import PriceFooterBar, { FooterPriceLine } from './UI/PriceFooterBar';
import TaxModeSwitch from './UI/TaxModeSwitch';
import { readShowGross, writeShowGross } from '../utils/priceFormat';
import { parseMotorRules } from '../config/priceConfigComfort';
import { applyWalkOnRuntimeConfig } from '../config/priceConfigWalkOn';
import { NeedsAnswers } from '../config/needsFinder';

const ProductConfigAdmin: React.ComponentType | null =
  process.env.NODE_ENV !== 'production'
    ? require('./Admin/ProductConfigAdmin').default
    : null;

type ProductDefaults = {
  [key: string]: {
    dimensions: Dimensions;
    modules: Modules;
    glassType: string | null;
    ralColor: string;
    ralColorAccessory?: string;
    accessories: string[];
    totalPrice: number;
  };
};

// (retained for parity with legacy code paths; unused by the modern first viewport)
const PRODUCT_DEFAULTS: ProductDefaults = {
  'SkyVision FIXED': {
    dimensions: { width: 300, length: 300, height: 210, diameter: 0 },
    modules: { fixed: 0, opening: 0 },
    glassType: null,
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision CIRCULAR': {
    dimensions: { width: 600, length: 600, height: 240, diameter: 600 },
    modules: { fixed: 0, opening: 0 },
    glassType: 'double',
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision CIRCULAR WALK-ON': {
    dimensions: { width: 600, length: 600, height: 210, diameter: 600 },
    modules: { fixed: 0, opening: 0 },
    glassType: 'double',
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision WALKON': {
    dimensions: { width: 600, length: 600, height: 210, diameter: 0 },
    modules: { fixed: 0, opening: 0 },
    glassType: 'double',
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision LINEAR WALK-ON': {
    dimensions: { width: 1000, length: 1000, height: 210, diameter: 0 },
    modules: { fixed: 1, opening: 0 },
    glassType: 'double',
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision LINEAR': {
    dimensions: { width: 1000, length: 2000, height: 210, diameter: 0 },
    modules: { fixed: 1, opening: 0 },
    glassType: null,
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision ACCESS': {
    dimensions: { width: 1000, length: 1000, height: 291, diameter: 0 },
    modules: { fixed: 0, opening: 0 },
    glassType: 'double',
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
  'SkyVision COMFORT': {
    dimensions: { width: 600, length: 600, height: 210, diameter: 0 },
    modules: { fixed: 0, opening: 0 },
    glassType: null,
    ralColor: 'RAL 7043',
    accessories: [],
    totalPrice: 0,
  },
};

const initialState: ConfiguratorState = {
  step: 1,
  product: '',
  glassType: null,
  dimensions: { width: 0, length: 0, height: 0, diameter: 0 },
  modules: { fixed: 1, opening: 0 },
  accessories: [],
  ralColor: 'RAL 7043',
  totalPrice: 0,
  formData: { installation: false, measurement: false, technicalDrawing: false },
  appliedDiscount: null,
};

interface KonfiguratorConfig {
  products: string;
  showVisualizer: boolean;
  theme: string;
  compact: boolean;
}

interface ConfiguratorProps {
  config?: KonfiguratorConfig;
  containerId?: string;
}

/** Beibehalten als Kompatibilitätstyp — die Foto/Riss-Umschaltung ist im
 *  neuen Interface entfallen; einzelne Legacy-Screens erwarten aber noch
 *  den Typ. */
export type ViewMode = 'riss' | 'foto';

interface StepDef {
  name: string;        // short label for the register / progress
  sheetTitle: string;  // full step name for the price bar
  component: React.ComponentType<any>;
}

function footerLinesFromApi(
  breakdown: { item?: string; price?: number }[],
  product: string,
): { lines: FooterPriceLine[]; net: number } {
  const mapped = mapApiBreakdownToPriceLines(breakdown, product);
  return {
    lines: mapped
      .filter(
        (line): line is typeof line & { net: number } =>
          line.net != null && line.net > 0,
      )
      .map((line) => ({
        id: line.id,
        label: line.detail ? `${line.label} · ${line.detail}` : line.label,
        net: line.net,
      })),
    net: sumPriceLines(mapped),
  };
}

export default function Configurator({ config, containerId }: ConfiguratorProps) {
  const [state, setState] = useState<ConfiguratorState>(initialState);
  const [offerFormOpen, setOfferFormOpen] = useState(false);
  const [priceLines, setPriceLines] = useState<FooterPriceLine[]>([]);
  const [priceStatus, setPriceStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [priceError, setPriceError] = useState<string | null>(null);
  const [priceRetry, setPriceRetry] = useState(0);
  const [accessoryCatalog, setAccessoryCatalog] = useState<AccessoryCatalogItem[]>([]);
  const [showGross, setShowGross] = useState(readShowGross);
  const footerCtaRef = useRef<HTMLButtonElement | null>(null);
  const defaultsAppliedFor = useRef('');
  const skipIntake = Boolean(config?.products && config.products !== 'all');
  const [phase, setPhase] = useState<'intake' | 'configure'>(
    skipIntake ? 'configure' : 'intake',
  );
  const [needAnswers, setNeedAnswers] = useState<NeedsAnswers>({});
  const [fromRecommendation, setFromRecommendation] = useState(false);
  const [wizardIndex, setWizardIndex] = useState(0);
  const [wizardTotal, setWizardTotal] = useState(3);
  const [catalogStartingPrices, setCatalogStartingPrices] = useState<Record<string, number>>({});

  const setTaxGross = useCallback((next: boolean) => {
    setShowGross(next);
    writeShowGross(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/wp-json/skyvision/v1/products')
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        const next: Record<string, number> = {};
        data.forEach((product: { id?: string; startingPrice?: number }) => {
          if (product.id && typeof product.startingPrice === 'number' && product.startingPrice > 0) {
            next[product.id] = product.startingPrice;
          }
        });
        setCatalogStartingPrices(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const updateState = (updates: Partial<ConfiguratorState>) => {
    setState((prev) => ({
      ...prev,
      ...updates,
      dimensions: updates.dimensions
        ? { ...prev.dimensions, ...updates.dimensions }
        : prev.dimensions,
      formData: { ...prev.formData, ...updates.formData },
    }));
  };

  useEffect(() => {
    if (!state.product) {
      setAccessoryCatalog([]);
      defaultsAppliedFor.current = '';
      return;
    }
    let cancelled = false;
    const code = productCodeFromName(state.product);
    setAccessoryCatalog([]);
    priceService
      .getAccessories(code)
      .then((items) => {
        if (!cancelled) setAccessoryCatalog(items);
      })
      .catch(() => {
        if (!cancelled) setAccessoryCatalog([]);
      });
    fetch(`/wp-json/skyvision/v1/products/${code}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (
          (code === 'skyvision-walkon' ||
            code === 'skyvision-linear-walkon' ||
            code === 'skyvision-circular-walkon') &&
          data.priceConfig
        ) {
          applyWalkOnRuntimeConfig({
            ...data.priceConfig,
            maxArea: data.maxArea ?? data.max_area,
          });
        }
        const nextRules = parseMotorRules(data.motorRules);
        setState((prev) => {
          const current = prev.motorRules;
          if (
            current &&
            current.dualAreaDouble === nextRules.dualAreaDouble &&
            current.dualAreaTriple === nextRules.dualAreaTriple &&
            current.dualMaxSide === nextRules.dualMaxSide
          ) {
            return prev;
          }
          return { ...prev, motorRules: nextRules };
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [state.product]);

  useEffect(() => {
    if (!state.product || state.step !== 4) return;
    let cancelled = false;
    priceService
      .getAccessories(productCodeFromName(state.product), { refresh: true })
      .then((items) => {
        if (!cancelled) setAccessoryCatalog(items);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [state.product, state.step]);

  useEffect(() => {
    if (!state.product || accessoryCatalog.length === 0) return;
    const applyDefaults = defaultsAppliedFor.current !== state.product;
    if (applyDefaults) defaultsAppliedFor.current = state.product;
    setState((prev) => {
      const next = applyCatalogSelection(
        prev.accessories || [],
        accessoryCatalog,
        prev.glassType,
        applyDefaults,
      );
      if (
        next.length === (prev.accessories || []).length &&
        next.every((code, index) => code === prev.accessories[index])
      ) {
        return prev;
      }
      return { ...prev, accessories: next };
    });
  }, [accessoryCatalog, state.product, state.glassType]);

  // Laufender Preis: ausschließlich vom Backend. Kein lokaler Fallback.
  useEffect(() => {
    const glassType = state.glassType;
    const hasMeasure =
      state.dimensions.width > 0 ||
      state.dimensions.length > 0 ||
      (state.dimensions.diameter || 0) > 0;
    const canPrice =
      Boolean(state.product) &&
      (glassType === 'double' || glassType === 'triple' || glassType === 'none') &&
      hasMeasure;

    if (!canPrice) {
      setPriceLines([]);
      setPriceStatus('idle');
      setPriceError(null);
      setState((prev) =>
        prev.totalPrice === 0 ? prev : { ...prev, totalPrice: 0 },
      );
      return;
    }

    let cancelled = false;
    setPriceStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    setPriceError(null);

    const timer = window.setTimeout(async () => {
      try {
        const result = await priceService.calculatePrice({
          product: productCodeFromName(state.product),
          dimensions: {
            width: state.dimensions.width,
            length: state.dimensions.length,
            height: state.dimensions.height,
            diameter: state.dimensions.diameter,
          },
          glassType: glassType as 'double' | 'triple' | 'none',
          accessories: state.accessories || [],
          ralColorOutside: state.ralColor,
          ralColorInside: state.ralColorInside,
          controlType: state.controlType,
        });
        if (cancelled) return;
        const fromApi = footerLinesFromApi(result.breakdown || [], state.product);
        const net = result.total_price;
        setPriceLines(fromApi.lines);
        setPriceStatus('ready');
        setPriceError(null);
        setState((prev) =>
          Math.abs((prev.totalPrice || 0) - net) < 0.01
            ? prev
            : { ...prev, totalPrice: net },
        );
      } catch (err) {
        if (cancelled) return;
        setPriceLines([]);
        setPriceStatus('error');
        setPriceError(
          err instanceof Error && err.message
            ? err.message
            : 'Preis konnte nicht vom Backend geladen werden.',
        );
        setState((prev) =>
          prev.totalPrice === 0 ? prev : { ...prev, totalPrice: 0 },
        );
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    state.product,
    state.dimensions,
    state.glassType,
    state.accessories,
    state.ralColor,
    state.ralColorInside,
    state.controlType,
    state.modules,
    priceRetry,
  ]);

  const nextStep = useCallback(() => setState((p) => ({ ...p, step: p.step + 1 })), []);
  const previousStep = useCallback(() => setState((p) => ({ ...p, step: p.step - 1 })), []);
  const goToStep = useCallback((stepIndex: number) => setState((p) => ({ ...p, step: stepIndex + 1 })), []);

  const skipToCatalog = useCallback(() => {
    setFromRecommendation(false);
    setPhase('configure');
    setState((p) => ({
      ...p,
      step: 1,
      product: '',
      glassType: null,
      dimensions: { width: 0, length: 0, height: 0, diameter: 0 },
      modules: { fixed: 1, opening: 0 },
      accessories: [],
      totalPrice: 0,
    }));
  }, []);

  const chooseFromFinder = useCallback(() => {
    setFromRecommendation(true);
    setPhase('configure');
    setState((p) => ({ ...p, step: 1 }));
  }, []);

  const handleBack = useCallback(() => {
    if (phase === 'intake') {
      setWizardIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (state.step === 1 && !skipIntake) {
      setPhase('intake');
      return;
    }
    previousStep();
  }, [phase, state.step, skipIntake, previousStep]);

  const allSteps: StepDef[] =
    state.product === 'SkyVision LINEAR' || state.product === 'SkyVision LINEAR WALK-ON'
      ? [
          { name: 'Produkt',         sheetTitle: 'Produktauswahl',    component: ProductSelection },
          { name: 'Module',          sheetTitle: 'Modulauswahl',      component: ModuleSelection },
          { name: 'Verglasung',      sheetTitle: 'Verglasung',        component: GlassType },
          { name: 'Zubehör',         sheetTitle: 'Zubehör',           component: Accessories },
          { name: 'Zusammenfassung', sheetTitle: 'Zusammenfassung',   component: Summary },
        ]
      : [
          { name: 'Produkt',         sheetTitle: 'Produktauswahl',    component: ProductSelection },
          { name: 'Verglasung',      sheetTitle: 'Verglasung',        component: GlassType },
          { name: 'Maße',            sheetTitle: 'Maße',              component: SizeSelection },
          { name: 'Zubehör',         sheetTitle: 'Zubehör',           component: Accessories },
          { name: 'Zusammenfassung', sheetTitle: 'Zusammenfassung',   component: Summary },
        ];
  const steps = fromRecommendation ? allSteps.slice(1) : allSteps;

  const currentStepIdx = state.step - 1;
  const isFirstStep = state.step === 1;
  const isLastStep = state.step === steps.length;
  const currentSheetTitle = steps[currentStepIdx]?.sheetTitle ?? '';
  const isProductStep = steps[currentStepIdx]?.component === ProductSelection;
  const isModuleStep = currentSheetTitle === 'Modulauswahl';
  const isIntake = phase === 'intake';
  const intakeAtResults = isIntake && wizardTotal > 0 && wizardIndex >= wizardTotal;
  const intakeLabel = intakeAtResults
    ? 'Empfehlung'
    : wizardTotal > 0
      ? `Frage ${Math.min(wizardIndex + 1, wizardTotal)} von ${wizardTotal}`
      : 'Orientierung';

  // Migrierte Schritte, die die eigene Preisleiste über updateState füttern
  // und keine Legacy-Sidebar mehr brauchen.
  const isModernStep =
    isIntake ||
    isFirstStep ||
    currentSheetTitle === 'Verglasung' ||
    currentSheetTitle === 'Maße' ||
    currentSheetTitle === 'Modulauswahl' ||
    currentSheetTitle === 'Zubehör' ||
    currentSheetTitle === 'Zusammenfassung';

  // Nav-Konfiguration für die sticky Preisleiste.
  const nav = useMemo(() => {
    if (isIntake) {
      return {
        nextLabel: 'Weiter',
        onNext: undefined,
        nextDisabled: true,
        hideNext: true,
      };
    }
    if (isLastStep) {
      return {
        nextLabel: 'Unverbindliches Angebot',
        onNext: offerFormOpen ? undefined : () => setOfferFormOpen(true),
        nextDisabled: priceStatus !== 'ready' || !(state.totalPrice > 0),
        hideNext: offerFormOpen,
      };
    }
    if (isProductStep) {
      return {
        nextLabel: 'Weiter',
        onNext: nextStep,
        nextDisabled: !state.product,
        hideNext: false,
      };
    }
    if (isModuleStep) {
      const totalModules = (state.modules.fixed || 0) + (state.modules.opening || 0);
      const height = state.dimensions.height;
      return {
        nextLabel: 'Weiter',
        onNext: nextStep,
        nextDisabled: totalModules < 1 || height < 210 || height > 750,
        hideNext: false,
      };
    }
    const nextName = steps[state.step]?.sheetTitle;
    if (!nextName) return { nextLabel: 'Weiter', onNext: undefined, nextDisabled: false, hideNext: true };
    return {
      nextLabel: 'Weiter',
      onNext: nextStep,
      nextDisabled: false,
      hideNext: false,
    };
  }, [isIntake, isProductStep, isModuleStep, state.step, state.product, state.modules, state.dimensions.height, steps, isLastStep, nextStep, offerFormOpen, priceStatus, state.totalPrice]);

  useEffect(() => {
    if (!isLastStep) setOfferFormOpen(false);
  }, [isLastStep]);

  // Nach Auswahl auf Seite 1: sanft zum sticky-CTA scrollen und pulsen.
  const focusFooterCta = useCallback(() => {
    if (typeof window === 'undefined') return;
    const btn = footerCtaRef.current;
    if (!btn) return;
    btn.classList.add('is-pulsing');
    window.setTimeout(() => btn.classList.remove('is-pulsing'), 1200);
  }, []);

  const renderStep = () => {
    const StepComponent = steps[currentStepIdx]?.component;
    if (!StepComponent) {
      return (
        <div className="p-8 text-rubric text-[13px]">
          Fehler — Schritt {state.step} nicht gefunden.
        </div>
      );
    }
    const props = {
      state,
      updateState,
      onNext: nextStep,
      onBack: state.step > 1 ? previousStep : !skipIntake ? handleBack : undefined,
      config,
      viewMode: 'riss' as ViewMode,
      onSelectionMade: focusFooterCta,
      showOfferForm: offerFormOpen,
      onShowOfferForm: setOfferFormOpen,
      showGross,
      catalog: accessoryCatalog,
      onCatalogChange: setAccessoryCatalog,
      priceError,
      priceReady: priceStatus === 'ready',
    };
    return <StepComponent {...props} />;
  };

  return (
    <div className="sv-sheet-plain min-h-screen flex flex-col">
      {/* ---- Header (schlank) ---- */}
      <header
        className="sv-hairline-b"
        style={{ borderColor: '#D1D5D2' }}
        role="banner"
      >
        <div className="mx-auto grid grid-cols-[auto_1fr_auto] items-center gap-4 md:gap-6 px-4 md:px-6 py-4 md:py-5 max-w-[1600px]">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-[16px] font-medium text-ink whitespace-nowrap" style={{ letterSpacing: '0.08em' }}>
              GLASOLUX
              <span className="mx-2 opacity-40 hidden sm:inline">·</span>
              <span className="tracking-[0.14em] hidden sm:inline">SKYVISION</span>
            </span>
          </div>

        {isIntake ? (
          <p className="sv-mono text-center text-[13px] opacity-70 m-0">{intakeLabel}</p>
        ) : (
          <StepIndicator
            steps={steps.map((s) => ({ name: s.name }))}
            currentStep={currentStepIdx}
            onStepChange={goToStep}
          />
        )}

          <TaxModeSwitch showGross={showGross} onChange={setTaxGross} />
        </div>
      </header>

      {/* ---- Sheet body ---- */}
      <main
        className="mx-auto w-full px-4 md:px-6 py-8 md:py-10 max-w-[1600px] flex-1"
      >
        {!skipIntake && (
          <div hidden={!isIntake}>
            <NeedsFinder
              updateState={updateState}
              config={config}
              showGross={showGross}
              answers={needAnswers}
              onAnswersChange={setNeedAnswers}
              onSkipToCatalog={skipToCatalog}
              onChooseProduct={chooseFromFinder}
              wizardIndex={wizardIndex}
              onWizardIndexChange={setWizardIndex}
              onWizardTotalChange={setWizardTotal}
            />
          </div>
        )}
        {!isIntake && (isModernStep ? (
          <div key={state.step}>{renderStep()}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)] gap-10 lg:items-start">
            <div className="sv-legacy-font">{renderStep()}</div>
            <aside className="space-y-6 sv-legacy-font">
              {config?.showVisualizer !== false && <ProductVisualizer state={state} />}
              <PriceDisplay
                state={state}
                formData={state.formData}
                appliedDiscount={state.appliedDiscount}
                showGross={showGross}
              />
            </aside>
          </div>
        ))}
      </main>

      {/* ---- Sticky Preis- & Navigationsleiste ---- */}
      <PriceFooterBar
        state={state}
        lines={priceLines}
        showGross={showGross}
        stepNumber={isIntake ? 0 : state.step}
        stepCount={steps.length}
        currentStepName={isIntake ? intakeLabel : currentSheetTitle}
        nextStepName={!isIntake && !isLastStep ? steps[state.step]?.sheetTitle : undefined}
        onBack={
          isIntake
            ? wizardIndex > 0
              ? handleBack
              : undefined
            : state.step > 1 || !skipIntake
              ? handleBack
              : undefined
        }
        onNext={nav.onNext}
        nextLabel={nav.nextLabel}
        nextDisabled={nav.nextDisabled}
        hideNext={nav.hideNext}
        hideBack={isIntake ? wizardIndex === 0 : false}
        nextRef={footerCtaRef}
        priceStatus={priceStatus}
        priceError={priceError}
        onRetryPrice={() => setPriceRetry((n) => n + 1)}
        startingPrice={
          state.product
            ? catalogStartingPrices[productCodeFromName(state.product)]
            : Object.values(catalogStartingPrices).reduce<number | undefined>(
                (min, value) => (min === undefined || value < min ? value : min),
                undefined,
              )
        }
      />

      {ProductConfigAdmin ? <ProductConfigAdmin /> : null}
    </div>
  );
}
